import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const initialCheckDone = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();

    const checkAuthStatus = useCallback(async () => {
        // Skip auth check if we're on the login page or public routes
        if (location.pathname === '/admin/login' || location.pathname === '/admin' || location.pathname.startsWith('/admin/public')) {
            console.debug('[AuthContext] Skipping auth check on public route');
            return;
        }

        console.debug('[AuthContext] Checking auth status...');
        setLoading(true);
        try {
            const response = await authApi.me();
            if (response.data?.data?.user) {
                console.debug('[AuthContext] User is authenticated');
                setUser(response.data.data.user);
                setError(null);
            } else {
                console.debug('[AuthContext] No user data in response');
                setUser(null);
                navigate('/admin/login', { 
                    replace: true,
                    state: { from: location.pathname }
                });
            }
        } catch (err) {
            console.error('[AuthContext] Auth status check failed:', err.message);
            setUser(null);
            setError('Authentication failed. Please log in again.');
            navigate('/admin/login', { 
                replace: true,
                state: { from: location.pathname }
            });
        } finally {
            setLoading(false);
        }
    }, [navigate, location.pathname]);

    // Initialize auth state
    useEffect(() => {
        if (initialCheckDone.current) return;
        
        const initAuth = async () => {
            console.debug('[AuthContext] Initializing auth state...');
            setLoading(true);
            
            // Skip auth check if on login page
            if (location.pathname === '/admin/login') {
                console.debug('[AuthContext] On login page, skipping auth check');
                setUser(null);
                setLoading(false);
                setIsInitialized(true);
                initialCheckDone.current = true;
                return;
            }
            
            try {
                const response = await authApi.me();
                if (response.data?.data?.user) {
                    console.debug('[AuthContext] User authenticated during initialization');
                    setUser(response.data.data.user);
                } else {
                    console.debug('[AuthContext] Invalid user data during initialization');
                    setUser(null);
                    if (location.pathname !== '/admin' && !location.pathname.startsWith('/admin/public')) {
                        navigate('/admin/login', { 
                            replace: true,
                            state: { from: location.pathname }
                        });
                    }
                }
            } catch (err) {
                console.error('[AuthContext] Auth initialization error:', err.message);
                setUser(null);
                if (location.pathname !== '/admin' && !location.pathname.startsWith('/admin/public')) {
                    navigate('/admin/login', { 
                        replace: true,
                        state: { from: location.pathname }
                    });
                }
            } finally {
                setLoading(false);
                setIsInitialized(true);
                initialCheckDone.current = true;
            }
        };
        
        initAuth();
    }, [navigate, location.pathname]);

    // Check auth status on pathname changes
    useEffect(() => {
        if (isInitialized && 
            location.pathname !== '/admin/login' && 
            location.pathname !== '/admin' && 
            !location.pathname.startsWith('/admin/public')) {
            console.debug('[AuthContext] Checking auth status due to pathname change:', location.pathname);
            checkAuthStatus();
        }
    }, [location.pathname, checkAuthStatus, isInitialized]);

    // Listen for auth errors
    useEffect(() => {
        const handleAuthError = () => {
            console.debug('[AuthContext] Auth error event received');
            setUser(null);
            setError('Session expired. Please log in again.');
            
            if (location.pathname !== '/admin/login' && 
                location.pathname !== '/admin' && 
                !location.pathname.startsWith('/admin/public')) {
                navigate('/admin/login', { 
                    replace: true,
                    state: { from: location.pathname }
                });
            }
        };

        window.addEventListener('auth-error', handleAuthError);
        return () => window.removeEventListener('auth-error', handleAuthError);
    }, [navigate, location.pathname]);

    const login = async (email, password) => {
        console.debug('[AuthContext] Attempting login...');
        try {
            setError(null);
            setLoading(true);
            
            const response = await authApi.login({ email, password });
            console.debug('[AuthContext] Login response:', { 
                success: response.data.status === 'success',
                hasToken: !!response.data.data.token
            });
            
            const { data } = response.data;
            if (data?.user) {
                setUser(data.user);
                
                // Store token if provided
                if (data.token) {
                    localStorage.setItem('token', data.token);
                    console.debug('[AuthContext] Token stored in localStorage');
                }
                
                const from = location.state?.from || '/dashboard';
                navigate(from, { replace: true });
                return response.data;
            } else {
                console.error('[AuthContext] Invalid response from server');
                throw new Error('Invalid response from server: missing user data');
            }
        } catch (err) {
            console.error('[AuthContext] Login error:', err.message);
            setError(err.response?.data?.message || 'Login failed. Please try again.');
            setUser(null);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = useCallback(async () => {
        console.debug('[AuthContext] Attempting logout...');
        try {
            await authApi.logout();
            setUser(null);
            localStorage.removeItem('token'); // Clear token on logout
            navigate('/login', { replace: true });
        } catch (err) {
            console.error('[AuthContext] Logout error:', err.message);
            // Even if the server request fails, we'll still clear the local state
            setUser(null);
            localStorage.removeItem('token'); // Clear token even on error
            navigate('/login', { replace: true });
        }
    }, [navigate]);

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        isInitialized,
        checkAuthStatus
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext; 